import { fetchRcaRelatedWorkorders } from "../../../../actions/qc";

/**
 * GET rca_related_workorders/ params (Data Ops RCA details / Add RCA / history Edit).
 * Response shape matches getrelatedinfo/ — reuse the same row mapper.
 */
export const buildRcaRelatedWorkordersParams = (row = {}) => ({
  genista_id: row.genista_id ?? null,
  analysis: row.analysis ?? row.test_name ?? row.test ?? row.internal_name ?? null,
});

export const hasRcaRelatedWorkordersParams = (row = {}) => {
  const params = buildRcaRelatedWorkordersParams(row);
  return Boolean(
    params.genista_id
    && String(params.genista_id).trim() !== ""
    && params.analysis
    && String(params.analysis).trim() !== "",
  );
};

const flattenRelatedInfoTest = (workorder, customer, sample, test) => ({
  tis_pk: test.tis_pk,
  test_id_pk: test.id_pk,
  workorder_id: workorder.id,
  wo_id_pk: workorder.pk,
  customer_id: customer.id,
  customer_id_pk: customer.pk,
  company_name: customer.name,
  genista_id: sample.genista_id,
  sample_id: sample.sample_id,
  sample_type: sample.type,
  analysis: test.analysis,
  internal_name: test.analysis,
  test_category: test.category,
  result: test.result,
  result_status: test.status,
  retest_reason: test.retest_reason,
});

/**
 * Flat read-only rows (one row per test × workorder) — original + retest WOs visible without expand.
 * @param {object[]} apiResult
 * @returns {object[]}
 */
export const mapGetRelatedInfoToWorkorderRows = (apiResult = []) => {
  if (!Array.isArray(apiResult)) {
    return [];
  }

  const rows = [];

  apiResult.forEach((item) => {
    const { workorder, customer, sample } = item;
    const tests = sample?.tests ?? [];

    tests.forEach((test, testIndex) => {
      const flatTest = flattenRelatedInfoTest(workorder, customer, sample, test);
      rows.push({
        ...flatTest,
        is_retest: workorder.is_retest,
        key: `${workorder.pk}-${test.tis_pk ?? testIndex}`,
      });
    });
  });

  return rows;
};

const describeMissingRcaRelatedWorkordersParams = (row = {}) => {
  const params = buildRcaRelatedWorkordersParams(row);
  const missing = [];
  if (!params.genista_id || String(params.genista_id).trim() === "") {
    missing.push("Genista ID");
  }
  if (!params.analysis || String(params.analysis).trim() === "") {
    missing.push("test name");
  }
  if (!missing.length) {
    return null;
  }
  return `Missing ${missing.join(", ")} for related workorder table display.`;
};

/**
 * Load related workorders for Data Ops RCA details modal (Add / Edit / history Edit).
 * @param {object} row — Add RCA or tracking row
 * @returns {Promise<{ success: boolean, rows: object[], message?: string }>}
 */
export const fetchRcaRelatedWorkorderRows = async (row = {}) => {
  const missingMessage = describeMissingRcaRelatedWorkordersParams(row);
  if (missingMessage) {
    return {
      success: false,
      rows: [],
      message: missingMessage,
    };
  }

  const params = buildRcaRelatedWorkordersParams(row);
  const response = await fetchRcaRelatedWorkorders(params);

  if (!response.success) {
    return {
      success: false,
      rows: [],
      message: response.message || "Failed to load related workorder data",
    };
  }

  let resultList = [];
  if (Array.isArray(response.result)) {
    resultList = response.result;
  } else if (Array.isArray(response.results)) {
    resultList = response.results;
  }

  return {
    success: true,
    rows: mapGetRelatedInfoToWorkorderRows(resultList),
  };
};
